import React, { useCallback, useMemo, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { use100vh } from 'react-div-100vh';
import toast from 'react-hot-toast';
import { useLocation } from 'react-router-dom';
import AuthForm from 'components/auth/tools/AuthForm';
import NiceModal from 'components/modals/NiceModal';
import VideoModal from 'components/modals/VideoModals';
import { LOGIN_MODE } from 'constants/auth';
import useKeyboardInputRef from 'hooks/useKeyboardInputRef';
import { useScreen } from 'hooks/useScreen';
import ls from 'local-storage';
import { useMappedState } from 'redux-react-hook';
import styled from 'styled-components';
import { isBlank } from 'utils/storeUtils';

import x_img from '../../../assets/exit.svg';
import ReactHelper from '../../../helpers/ReactHelper';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import EmailConfirmationModal from '../../modals/EmailConfirmationModal';
import PictureModal from '../../modals/PictureModal';
import SubscriptionModal from '../../modals/SubscriptionModal';
import CharacterAdditionalInfo from '../tools/CharacterAdditionalInfo';
import { DialogSettingsTool } from '../tools/DialogSettingsTool';
import SignupDialogOverlayTool from '../tools/SignupDialogOverlayTool';
import UserMessageTool from '../tools/UserMessageTool';
import BackButton from './components/Actions/BackButton';
import DialogSettings from './components/Actions/DialogSettings';
import RestartButton from './components/Actions/RestartButton';
import AdminTopPanel from './components/AdminTopPanel';
import CharactersDialogAsset from './components/CharactersDialogAsset';
import AllMessageSwitcher from './components/Messages/AllMessageSwitcher';
import InteractiveUserMessage from './components/Messages/InteractiveUserMessage';
import MessageActions from './components/Messages/MessageActions';
import MobileDialogTitle from './components/MobileDialogTitle';
import MobileNavigationPanel from './components/MobileNavigationPanel';
import ChangeNameModal from './components/modals/ChangeNameModal';
import DeleteMessageConfirmModal from './components/modals/DeleteMessageConfirmModal';
import ResetDialogModal from './components/modals/ResetDialogModal';
import SkeletonBatchLoader from './components/SkeletonBatchLoader';
import UserInputComponent from './components/UserInputComponent/UserInputComponent';
import useAutoScrollOnLoad from './hooks/useAutoScrollOnLoad';
import useChatMode from './hooks/useChatMode';
import useCheckUserBlocks from './hooks/useCheckUserBlocks';
import useDialogData from './hooks/useDialogData';
import useDialogSetModals from './hooks/useDialogSetModals';
import useSendDialogMessage from './hooks/useSendDialogMessage';
import { checkMessageOnBlockers, scrollToBottom } from './helpers';
import { isCursorBetweenBrackets } from './utils';

import 'react-tooltip/dist/react-tooltip.css';

const DialogRootPanel = (props) => {
  const {
    id,
    scenarioId,
    alias,
    autoScroll = true,
    hasAdminHead = false,
    hasInput = true,
    canDeleteMessages = true,
    canClickOnBatch = false,
    showScenarioInfo = true,
    showSettings = true,
    canEdit = true,
    characterName,
    onBatchClick = () => { },
    onNameChange = () => { }
  } = props;
  const messagesEndRef = useRef(null);
  const location = useLocation();

  const [totalBathesNumber, setTotalBatchesNumber] = useState(0); // все batches юзера
  const [customDialogsBatchesNumber, setCustomDialogsBatchesNumber] = useState(0); // все batches юзера в кастомных диалогах


  const [zoomMediaModalUrl, setZoomMediaModalUrl] = useState(null);

  const [videoModalState, setVideoModalState] = useState({
    isOpen: false,
    videoUrl: null,
    pictureUrl: null
  });

  const [hasSignUpBlock, setHasSignUpBlock] = useState(false);
  const [isSignUpModalOpen, setIsSignUpModalOpen] = useState(false);

  const [hasEmailConfirmationBlock, setHasEmailConfirmationBlock] = useState(false);
  const [isEmailConfirmationModalOpen, setIsEmailConfirmationModalOpen] = useState(false);

  const [hasSubscriptionBlock, setHasSubscriptionBlock] = useState(false);
  const [subscriptionModalState, setSubscriptionModalState] = useState({
    isOpen: false,
    renderedForReason: undefined,
    showScenarioButton: false
  });

  const [chatMode, setChatMode] = useState('chat');

  const [newMessageText, setNewMessageText] = useState('');
  const [dialog, setDialog] = useState(null);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [isContinueDialog, setIsContinueDialog] = useState(false);
  const [scenario, setScenario] = useState(null);
  const [game, setGame] = useState(null);
  const [tempText, setTempText] = useState('');
  const [updT, setUpdT] = useState(0);
  const [isRestartGame, setIsRestartGame] = useState(false);
  const [allTags, setAllTags] = useState([]);

  const [isSignInModalOpen, setIsSignInModalOpen] = useState(false);
  const [badModelCountdownVisible, setBadModalCountdownVisible] = useState(false);

  const [addingMore, setAddingMore] = useState(false);

  const [deletingIndex, setDeletingIndex] = useState(null);
  const [marketingData, setMarketingData] = useState(null);

  const [countdownSeconds, setCountdownSeconds] = useState(0);

  const [isRegenerating, setIsRegenerating] = useState(false);
  const [isResetDialogModalOpen, setIsResetDialogModalOpen] = useState(false);

  const [changeNameModal, setChangeNameModal] = useState(false);

  const [isInfoBlockVisible, setIsInfoBlockVisible] = useState(false);
  const [activeMobileNavigationTab, setActiveMobileNavigationTab] = useState('chat');

  const { keyboardOpen } = useKeyboardInputRef();

  const inputRef = useRef(null);

  const mobileHeight = use100vh();
  const { isMobileDialog } = useScreen();

  const currentPath = location.pathname;
  const isAdminPanel = currentPath.includes('scenario-debug');

  const characterNames = useMemo(() => {
    const characterNames = scenario?.characters?.filter(character => !character.isUser).map(character => character.name);
    return characterNames?.join(', ') ?? '';
  }, [scenario]);


  const { currentUser, hasSubscription, premiumUsage } = useMappedState(
    useCallback(state => {
      const currentUser = state.users.usersMap.get(state.users.currentUserId);
      const hasSubscription = !!currentUser?.hasSubscription;
      return {
        currentUser: state.users.usersMap.get(state.users.currentUserId),
        premiumUsage: currentUser ? currentUser.premiumUsage : {},
        hasSubscription: hasSubscription
      };
    }, [])
  );

  const suggestionUsage = premiumUsage?.suggestions || 0;

  const [newName, setNewName] = useState(
    currentUser?.characterName || ls.get('characterName') || ''
  );

  const filteredBatches = batches.filter(batch => !isBlank(batch));

  const canGenerateMore =
    (filteredBatches.length > 1 && !loading && !sending) || addingMore;


  ReactHelper.useInterval(() => {
    if (!badModelCountdownVisible || subscriptionModalState.isOpen) {
      return;
    }
    if (countdownSeconds <= 0) {
      setBadModalCountdownVisible(false);
      return;
    }
    setCountdownSeconds(+countdownSeconds - 1);
  }, 1000);



  useDocumentTitle(game?.publicName);

  useChatMode({ chatMode, newMessageText, inputRef, isCursorBetweenBrackets, setChatMode });

  const { dialogLimits } = useDialogData({
    id,
    alias,
    scenarioId,
    characterName,
    isAdminPanel,
    updT,
    loading,
    setLoading,
    allTags,
    setAllTags,
    dialog,
    setDialog,
    scenario,
    setScenario,
    game,
    setGame,
    batches,
    setBatches,
    setMarketingData,
    setTotalBatchesNumber,
    setCustomDialogsBatchesNumber,
  });

  useCheckUserBlocks({
    currentUser,
    totalBathesNumber,
    marketingData,
    game,
    hasSignUpBlock,
    setHasSignUpBlock,
    hasEmailConfirmationBlock,
    setHasEmailConfirmationBlock,
    hasSubscription,
    hasSubscriptionBlock,
    setHasSubscriptionBlock,
  });

  useAutoScrollOnLoad({
    autoScroll,
    sending,
    loading,
    scrollToBottom,
  });

  const handleCheckMessageOnBlockers = (eventType = '') => {
    if (checkMessageOnBlockers({
      hasSignUpBlock,
      hasEmailConfirmationBlock,
      hasSubscriptionBlock,
      currentUser,
      game,
      customDialogsBatchesNumber,
      marketingData,
      totalBathesNumber,
      sending,
      setIsSignUpModalOpen,
      setIsEmailConfirmationModalOpen,
      setSubscriptionModalState,
      eventType,
    })) {
      return true;
    }
  };

  const hasBadModel = filteredBatches.length > 5 && dialogLimits?.hasBadModel;

  const getFormattedTags = () => {
    const gameTags = game?.tags ?? [];
    return gameTags.map(tag => {
      const currentTag = allTags.find(currentTag => currentTag.name === tag);
      return { name: currentTag?.name, hasPage: currentTag?.hasPage ?? false };
    });
  };

  const gameWithFormattedTags = useMemo(
    () => ({ ...game, tags: getFormattedTags() }),
    [game, allTags]
  );

  const setModals = useDialogSetModals({
    currentUser,
    suggestionUsage,
    setIsSignUpModalOpen,
    setIsEmailConfirmationModalOpen,
    setSubscriptionModalState,
  });

  const handleCloseVideoModal = () => {
    setVideoModalState({ isOpen: false, videoUrl: null, pictureUrl: null });
  };

  const { handleSendMessage } = useSendDialogMessage({
    dialog,
    game,
    scenario,
    batches,
    setBatches,
    setNewMessageText,
    setTempText,
    setSending,
    setBadModalCountdownVisible,
    setCountdownSeconds,
    setIsContinueDialog,
    setTotalBatchesNumber,
    setCustomDialogsBatchesNumber,
    setUpdT,
    setChatMode,
  });

  const handleMessageSubmit = (messageText = newMessageText, isBadModel = hasBadModel, blocker = handleCheckMessageOnBlockers, isMediaMessage = false) => {
    handleSendMessage(messageText, isBadModel, blocker, sending, isMediaMessage);
  };

  const isAboutMobileNavigationActiveTab = activeMobileNavigationTab === 'about';
  const isChatMobileNavigationActiveTabOrDesktop = activeMobileNavigationTab === 'chat' || !isMobileDialog;
  const isCharacterNavigationActiveTab = activeMobileNavigationTab !== 'chat' || activeMobileNavigationTab !== 'about';

  return (
    <OuterWrapper>
      {!isMobileDialog && (
        <BackButton
          isPrivate={scenario?.isPrivate}
          isMobileDialog={false}
          isInfoBlockVisible={isInfoBlockVisible}
          setIsInfoBlockVisible={setIsInfoBlockVisible}
        />
      )}
      <ExtraWrapper>
        {showScenarioInfo && (
          <>
            <TopTilePlaceholder>
              <MobileDialogTitle
                characterNames={characterNames}
                {...(game || {})}
                onSignInModalOpen={() => setIsSignInModalOpen(true)}
                onSignUpModalOpen={() => setIsSignUpModalOpen(true)}
                onEmailConfirmationModalOpen={setIsEmailConfirmationModalOpen}
                onSubscriptionModalOpen={setSubscriptionModalState}
              />
            </TopTilePlaceholder>

            {isChatMobileNavigationActiveTabOrDesktop && isMobile && (
              <MobileControls>
                <RestartButton sending={sending} handleRestartClick={() => {
                  if (handleCheckMessageOnBlockers('reset_dialog_button_click')) {
                    return;
                  }
                  setIsResetDialogModalOpen(true);
                }} />

                <DialogSettingsTool
                  currentUser={currentUser}
                  hasSubscription={hasSubscription}
                  onChangeName={() => setChangeNameModal(true)}
                  onPremiumClick={() => {
                    setSubscriptionModalState({
                      isOpen: true,
                      renderedForReason: 'dialog_settings_premium_click'
                    });
                  }}
                  onAuthClick={() => setIsSignUpModalOpen(true)}
                />
              </MobileControls>
            )}
            {isMobile && (
              <MobileNavigationPanel
                characters={scenario?.characters}
                activeTab={activeMobileNavigationTab}
                onTabChange={setActiveMobileNavigationTab}
                scenarioId={scenario?.id}
                game={game}
              />
            )}
          </>
        )}
        {isMobileDialog && isAboutMobileNavigationActiveTab && (
          <CharactersDialogAsset
            scenario={scenario}
            game={game}
            dialog={dialog}
            gameWithFormattedTags={gameWithFormattedTags}
            isRestartGame={isRestartGame}
            setSubscriptionModalState={setSubscriptionModalState}
            setIsSignUpModalOpen={setIsSignUpModalOpen}
            setIsEmailConfirmationModalOpen={setIsEmailConfirmationModalOpen}
            isMobileView
          />
        )}
        {isCharacterNavigationActiveTab && (
          <CharacterAdditionalInfo
            characters={[scenario?.characters.find(character => character.name === activeMobileNavigationTab)]}
            setIsSubscriptionModalOpen={setSubscriptionModalState}
            setIsSignUpModalOpen={setIsSignUpModalOpen}
            setIsEmailConfirmationModalOpen={setIsEmailConfirmationModalOpen}
            activeMobileNavigationTab={activeMobileNavigationTab}
            game={game}
            scenario={scenario}
            currentUser={currentUser}
          />
        )}
        {isChatMobileNavigationActiveTabOrDesktop && (
          <Wrapper
            id="chat_wrapper"
            showScenarioInfo={showScenarioInfo}
            style={{
              height: isMobile
                ? showScenarioInfo
                  ? mobileHeight - 140 + 'px'
                  : mobileHeight + 'px'
                : ''
            }}
          >
            {showSettings && (
              <DialogSettings
                sending={sending}
                handleCheckMessageOnBlockers={handleCheckMessageOnBlockers}
                setIsResetDialogModalOpen={setIsResetDialogModalOpen}
                setChangeNameModal={setChangeNameModal}
                setSubscriptionModalState={setSubscriptionModalState}
                setIsSignUpModalOpen={setIsSignUpModalOpen}
              />
            )}

            {hasAdminHead && (
              <AdminTopPanel
                game={game}
                dialog={dialog}
                setIsResetDialogModalOpen={setIsResetDialogModalOpen}
              />)}
            <BatchesPlaceholder
              id="batches_wrapper"
              hasInput={hasInput}
              keyboardHeight={`45vh`}
              keyboardOpen={isMobile && keyboardOpen}
            >
              <SkeletonBatchLoader game={game} isRestartGame={isRestartGame} />
              {filteredBatches.map((batch, index) => {
                const { messages = [], userMessage } = batch;
                const isLast = index === filteredBatches.length - 1;
                let lastMessageIndex = -1;
                if (isLast) {
                  for (const i in messages) {
                    if (messages[i].type === 'character') {
                      lastMessageIndex = i;
                    }
                  }
                }
                return (
                  <div key={`i-${index}`}>
                    {!isBlank(userMessage) && (
                      <MyUserPlaceholder
                        className={`flex-items flex justify-end rounded p-2 pr-0 text-right ${canClickOnBatch ? 'cursor-pointer hover:bg-gray-800' : ''
                          }`}
                        onClick={() => {
                          if (!canClickOnBatch) {
                            return;
                          }
                          onBatchClick(batch);
                        }}
                      >
                        <div>
                          <div
                            className={'text-sm text-white'}
                            style={{ minWidth: 60, textAlign: 'right' }}
                          >
                            <div
                              className={'items center-items flex flex-row justify-end'}
                              style={{
                                fontFamily: 'Nunito Sans',
                                fontSize: '14px',
                                marginBottom: 10
                              }}
                            >
                              <NamePlaceholder>
                                You
                              </NamePlaceholder>
                            </div>
                          </div>
                          <MyUserDiv>
                            <div className="flex items-center" style={{ textAlign: 'left' }}>
                              {canDeleteMessages && isLast && !isBlank(userMessage) && (
                                <DeleteBatchImg
                                  src={x_img}
                                  alt={'delete message image'}
                                  style={{ marginRight: '10px' }}
                                  onClick={async () => {
                                    if (handleCheckMessageOnBlockers('delete_message_click')) {
                                      return;
                                    }
                                    if (sending) {
                                      toast.error('Please wait until the current message is sent');
                                      return;
                                    }
                                    setDeletingIndex(batch?.batchIndex);
                                  }}
                                />
                              )}
                              <div className="inline-block items-start">
                                <UserMessageTool text={userMessage} />
                              </div>
                            </div>
                          </MyUserDiv>
                        </div>
                      </MyUserPlaceholder>
                    )}

                    <div
                      className={'messages_placeholder'}
                      style={{
                        opacity:
                          index > 0 && index === batches.length - 1 && isRegenerating
                            ? 0.2
                            : 1
                      }}
                    >
                      {messages.map((message, i) => {
                        const isShowVideoAvatar = isContinueDialog && isLast;
                        const { type = 'text', role } = message;
                        let isLastBatchAndIsFirstMessage =
                          isLast && index !== 0 && i === Number(lastMessageIndex);
                        if (isLast && index !== 0 && messages.length === 1) {
                          isLastBatchAndIsFirstMessage = true;
                        }
                        let st = {};
                        if (isLastBatchAndIsFirstMessage) {
                          st = { display: 'flex', flexDirection: 'row', alignItems: 'center' };
                        }
                        const isNarrator = type === 'narrator';
                        return (
                          <div
                            key={`i-${i}-${message.id}`}
                            className={`message-placeholder role-${role} bold isLastBatchAndIsFirstMessage `}
                            style={st}
                          >
                            <div>
                              <AllMessageSwitcher
                                message={message}
                                onUnblurClick={() => setModals('unblur_photo_in_dialog')}
                                onZoomClick={setZoomMediaModalUrl}
                                isShowVideoAvatar={isShowVideoAvatar}
                                videoAvatarUrl={game?.videoAvatarUrl}
                                setVideoModalState={setVideoModalState}
                                scenario={scenario}
                                game={game}
                                dialog={dialog}
                                topRightComponent={
                                  <MessageActions
                                    isNarrator={isNarrator}
                                    isLastBatchAndIsFirstMessage={isLastBatchAndIsFirstMessage}
                                    sending={sending}
                                    addingMore={addingMore}
                                    canEdit={canEdit}
                                    isRegenerating={isRegenerating}
                                    loading={loading}
                                    userMessage={userMessage}
                                    setIsRegenerating={setIsRegenerating}
                                    dialog={dialog}
                                    setUpdT={setUpdT}
                                    game={game}
                                    scenario={scenario}
                                    checkIfBlockers={handleCheckMessageOnBlockers}
                                    setDeletingIndex={setDeletingIndex}
                                    setAddingMore={setAddingMore}
                                    scrollToBottom={scrollToBottom}
                                    batches={batches}
                                    setBatches={setBatches}
                                    setTempText={setTempText}
                                    setNewMessageText={setNewMessageText}
                                    canGenerateMore={canGenerateMore}
                                    batch={batch}
                                  />
                                }
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
              <InteractiveUserMessage
                tempText={tempText}
                badModelCountdownVisible={badModelCountdownVisible}
                countdownSeconds={countdownSeconds}
                setSubscriptionModalState={setSubscriptionModalState}
                game={game}
                scenario={scenario}
                dialog={dialog}
              />
              <div ref={messagesEndRef} style={{ marginTop: 40, marginBottom: 40 }} />
            </BatchesPlaceholder>
            {hasInput && (
              <UserInputComponent
                dialog={dialog}
                scenario={scenario}
                game={game}
                isPrivate={scenario?.isPrivate}
                batches={batches}
                hasCharactersWithLore={scenario?.hasCharactersWithLore}
                setNewMessageText={setNewMessageText}
                newMessageText={newMessageText}
                inputRef={inputRef}
                badModelCountdownVisible={badModelCountdownVisible}
                onMessageSubmit={handleMessageSubmit}
                chatMode={chatMode}
                setChatMode={setChatMode}
                sending={sending}
                suggestionUsage={suggestionUsage}
                setModals={setModals}
                initialSuggestion={scenario?.initialSuggestion}
              />
            )}
          </Wrapper >
        )}
      </ExtraWrapper >
      {showScenarioInfo && (
        <CharactersDialogAsset
          scenario={scenario}
          game={game}
          dialog={dialog}
          gameWithFormattedTags={gameWithFormattedTags}
          isRestartGame={isRestartGame}
          setSubscriptionModalState={setSubscriptionModalState}
          setIsSignUpModalOpen={setIsSignUpModalOpen}
          setIsEmailConfirmationModalOpen={setIsEmailConfirmationModalOpen}
          isMobileView={false}
          scenarioId={scenario?.id}
        />
      )}
      {isSignUpModalOpen && (
        <SignupDialogOverlayTool
          onClose={() => setIsSignUpModalOpen(false)}
          scenario={`${game?.alias}`}
        />
      )}{isSignInModalOpen && (
        <NiceModal onClose={() => setIsSignInModalOpen(false)}>
          <ModalInnerPlaceholder>
            <AuthForm mode={LOGIN_MODE} analyticsData={{ renderedForReason: 'dialog_sign_in_modal' }} onClose={() => setIsSignInModalOpen(false)} />
          </ModalInnerPlaceholder>
        </NiceModal>
      )

      }
      <EmailConfirmationModal
        isOpen={isEmailConfirmationModalOpen}
        onClose={() => setIsEmailConfirmationModalOpen(false)}
      />
      {subscriptionModalState.isOpen && (
        <SubscriptionModal
          isOpen
          analyticsData={{
            config_name: scenario?.name,
            scenario_name: game?.name,
            dialogId: dialog?.id,
            rendered_for_reason: subscriptionModalState.renderedForReason
          }}
          showScenarioButton={subscriptionModalState.showScenarioButton}
          onClose={() =>
            setSubscriptionModalState({
              isOpen: false,
              renderedForReason: undefined
            })
          }
        />)}
      <ResetDialogModal
        isOpen={isResetDialogModalOpen}
        setIsResetDialogModalOpen={setIsResetDialogModalOpen}
        setIsRestartGame={setIsRestartGame}
        setIsInfoBlockVisible={setIsInfoBlockVisible}
        setBatches={setBatches}
        setGame={setGame}
        setScenario={setScenario}
        setDialog={setDialog}
        dialog={dialog}
        game={game}
        alias={alias}
        scenarioId={scenarioId}
        characterName={characterName}
        scenario={scenario}
        isRestartGame={isRestartGame}
      />
      <DeleteMessageConfirmModal
        isOpen={deletingIndex}
        dialogId={dialog?.id}
        deletingIndex={deletingIndex}
        scenario={scenario}
        game={game}
        setBatches={setBatches}
        setSending={setSending}
        setUpdT={setUpdT}
        setDeletingIndex={setDeletingIndex}
      />
      <ChangeNameModal
        isOpen={changeNameModal}
        onClose={() => setChangeNameModal(false)}
        newName={newName}
        setNewName={setNewName}
        game={game}
        scenario={scenario}
        dialog={dialog}
        onNameChange={onNameChange}
        setUpdT={setUpdT}
      />
      {zoomMediaModalUrl && (
        <PictureModal onClose={() => setZoomMediaModalUrl(null)} src={zoomMediaModalUrl} />
      )}
      {videoModalState.isOpen && (
        <VideoModal
          onClose={handleCloseVideoModal}
          pictureUrl={videoModalState.pictureUrl}
          videoUrl={videoModalState.videoUrl}
        />
      )}
    </OuterWrapper >
  );
};

export default DialogRootPanel;

const TopTilePlaceholder = styled.div`
  display: none;
  margin-bottom: 20px;
  cursor: pointer;
  gap: 8px;

  @media (max-width: 720px) {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    margin-bottom: 0px;
  }
`;


const OuterWrapper = styled.div`
  height: 100%;
  max-height: 100%;
  box-sizing: border-box;
  width: 100%;
  display: flex;
  padding-bottom: 10px;
`;

const ExtraWrapper = styled.div`
  flex: 1;
  border: none;
  font-family: 'Nunito Sans';
  position: relative;
  max-height: 100%;
  width: 100%;
`;

const Wrapper = styled.div`
  background: rgba(29, 29, 29, 1);
  border-radius: 12px;
  border: none;
  font-family: Nunito Sans;
  position: relative;
  height: 100%;
  width: 100%;
  padding-right: 12px;
  padding-top: 24px;
  display: flex;
  flex-direction: column;
  @media (max-width: 720px) {
    height: ${props => (props?.showScenarioInfo ? 'calc(100% - 70px)' : '100%')};
    padding-bottom: 10px;
    border-radius: 0px 0px 12px 12px;
  }
`;

const BatchesPlaceholder = styled.div`
  padding: 0 24px;
  padding-top: 32px;
  padding-right: 12px;
  padding-bottom: ${props => (props?.hasInput ? '10px' : '0px')};
  margin-bottom: ${props => (props?.hasInput ? '82px' : '0px')};
  box-sizing: border-box;

  max-height: ${props => (props.isKeyboardOpen ? `calc(100% - ${props.keyboardHeight})` : '100%')};
  height: ${props => (props.isKeyboardOpen ? `calc(100% - ${props.keyboardHeight})` : '100%')};

  overflow-y: auto;
  @media (max-width: 720px) {
    padding-top: 0px;
    padding-left: 12px;
    padding-bottom: ${props => (props?.hasInput ? '6px' : '0px')};
    margin-bottom: ${props => (props?.hasInput ? '66px' : '0px')};
    overflow-x: hidden;
  }
`;

const MyUserPlaceholder = styled.div``;


const MyUserDiv = styled.div`
  max-width: 400px;
  background: rgba(70, 55, 50, 1);
  border-radius: 8px 4px 8px 8px;
  padding: 12px;
  padding-right: 20px;
  color: white;
  position: relative;
`;

const DeleteBatchImg = styled.img`
  cursor: pointer;
  opacity: 0.9;
  min-height: 24px;
  min-width: 24px;

  :hover {
    opacity: 1;
  }
`;

const NamePlaceholder = styled.div`
  font-family: Nunito Sans;
  font-size: 16px;
  line-height: 18px;
  font-weight: bold;
  color: rgba(255, 126, 85, 1);
`;

const ModalInnerPlaceholder = styled.div`
  width: 380px;
  padding: 24px;
  box-sizing: border-box;
  @media (max-width: 400px) {
    width: 100%;
  }
`;

const MobileControls = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  position: absolute;
  right: 20px;
  top: 158px;
  gap: 8px;
  z-index: 10;
`;
